import CircularProgress from '@material-ui/core/CircularProgress';
import { Field } from 'formik';
import { ROLES } from 'globalconfig/constants/keys';
import { getAuthDetails, hasRestrictedRole } from 'globalutils/fmUtils';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PolicyList from './Components/PolicyList';
import { languages } from './Components/PolicyList/config';
import { CONSTANTS } from './constants';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Policy = lazy(() => import(`@/modules/Policy/${layout}`));
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Upload = lazy(() => import(`globalcomponents/FileUpload/${layout}`));
const UploadCustomPolicy = lazy(() => import(`@/modules/CustomPolicyUpload`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);
const Modal = require(`globalcomponents/UI/Modal/${layout}`);
const beBaseUrl = window?.$environment?.BE_BASE_URL;
const userDetails = getAuthDetails();

const SubmitPolicyButton = ({ customProps, i18n, checkFields, values, validateForm, submitAction }) => {
  return (
    <FormStyle.default.ButtonsWrapper className="policySubmit">
      <Button
        type="submit"
        id="submitPolicy"
        label={customProps?.hasApiStatus?.isLoading ? <CircularProgress color="inherit" /> : i18n.t('common.submit')}
        onClick={() => {
          if (checkFields(values)) {
            const validateResponse = validateForm();
            validateResponse.then((data) => {
              submitAction(data);
            });
          }
        }}
      />
    </FormStyle.default.ButtonsWrapper>
  );
};

const RenderComponent = ({ values, handleEdit, errors, isNotValid, validateForm, customProps, submitAction }) => {
  const {
    logBook,
    policies,
    saveCustomPolicy,
    customPolicy,
    editCustomPolicy,
    handleDeleteCustomPolicyAlert,
    callback,
    fetchCustomPolicy,
    downloadCustomPolicy,
  } = customProps;

  const { i18n } = useTranslation();
  const [docLink, setDocLink] = useState();
  const [errFields, setErrFields] = useState({});
  const [open, setOpen] = useState(false);
  const [withCloseIcon, setwithCloseIcon] = useState(true);
  const [backgroundModal, setbackgroundModal] = useState(false);
  const [currentItem] = useState({});
  const {
    I18N_KEYS: {
      MAIN_INFO,
      COMPANY_LOGO,
      CHOOSE_IMAGE,
      SUBMIT,
      CUSTOM_POLICIES,
      SELECT_POLICIES,
      CUSTOM_POLICY_TEMPLATE,
      UPLOAD_NEW_DOC,
    },
    URLS: { CUSTOM_POLICY_TEMPLATE: CUSTOM_POLICY_TEMPLATE_URL },
    API_IDS: { FETCH_CUSTOM_POLICY, UPLOAD_CUSTOM_POLICY },
    MISC: { FILE_TYPES_NOT_ALLOWED, CUSTOM_POLICY_PARENT },
    FILE_TYPES: { IMAGES },
  } = CONSTANTS;

  const viewOnly = hasRestrictedRole([ROLES.VIEW_ONLY_FM]);
  const isSpireon = checkServiceProvider('isSpireon', window.$environment?.SERVICE_PROVIDER, false);
  const enableCustomPolicy = userDetails?.enterpriseFeatureDto?.customPoliciesEnabled && isSpireon;

  useEffect(() => {
    if (logBook?.documentToken && policies?.fileId) {
      setDocLink(beBaseUrl + '/document/open/' + policies?.fileId + '?token=' + logBook?.documentToken);
    }
  }, [logBook, policies]);

  useEffect(() => {
    if (enableCustomPolicy) {
      fetchCustomPolicy({
        apiId: FETCH_CUSTOM_POLICY,
      });
    }
  }, []);

  const [ModalObject, setModalObject] = useState({
    header: '',
    content: () => {},
  });

  const clickedModal = () => {
    setOpen(true);
  };
  const closeModal = () => {
    setOpen(false);
  };

  const checkFields = (formValues) => {
    if (checkServiceProvider('checkFields', window?.$environment?.SERVICE_PROVIDER, false)) return true;

    let errorFields = {};
    const { policyParameters = {} } = formValues;
    if (formValues.DRIVER_SELECTION_MVR) {
      if (!policyParameters.NUMBER_OF_ACCIDENTS_ALLOWED) errorFields.NUMBER_OF_ACCIDENTS_ALLOWED = true;
      if (!policyParameters.NUMBER_OF_CITATIONS_ALLOWED) errorFields.NUMBER_OF_CITATIONS_ALLOWED = true;
      if (!policyParameters.MIN_YEARS_ON_SIMILAR_EQUIPMENT) errorFields.MIN_YEARS_ON_SIMILAR_EQUIPMENT = true;
      if (!policyParameters.MIN_DRIVER_AGE) errorFields.MIN_DRIVER_AGE = true;
    }
    if (formValues.DRIVING_TRAINING) {
      if (!policyParameters.TRAINING_DAYS) errorFields.TRAINING_DAYS = true;
      if (!policyParameters.MIN_HOURS) errorFields.MIN_HOURS = true;
    }
    if (formValues.ACCIDENT_INVESTIGATION) {
      if (!policyParameters.SAFETY_PERSON_FULL_NAME) errorFields.SAFETY_PERSON_FULL_NAME = true;
    }
    setErrFields(errorFields);
    if (Object.keys(errorFields) == 0) return true;
  };

  return (
    <>
      {customProps.hasApiStatus?.errorMessage && <Message type="error" message={customProps.hasApiStatus.errorMessage} />}
      <div className="section-wrapper">
        <SubHeader variant="h2" className="titlePolicy maininfo" text={i18n.t(MAIN_INFO)} />
        <div className="policyLine">
          <div className="policyText">
            <p>{i18n.t(COMPANY_LOGO)}</p>
            <p>{i18n.t(CHOOSE_IMAGE)}</p>
          </div>
          <FormStyle.default.FieldsWrapper className="vertical">
            <FormStyle.default.TextWrapper>
              <Field
                name="upload"
                onUpload={(event) => {
                  handleEdit(event, { field: 'file', type: 'file' });
                }}
                id="upload"
                as={Upload}
                allowMultiple={false}
                totalFiles={1}
                placeholderText=""
                value={values.fileName}
                type="file"
                normal
                acceptedFileTypes={IMAGES}
                labelFileTypeNotAllowed={FILE_TYPES_NOT_ALLOWED}
                allowFileTypeValidation
                imageUpload
                disabled={viewOnly}
                docLink={docLink}
                fileChosen={(event) => {
                  if (event && event.length === 0) {
                    handleEdit(event, { field: 'file', type: 'file' });
                  }
                }}
              />

              {errors['upload'] && !isNotValid && <Message type="error" message={errors['upload']} />}
            </FormStyle.default.TextWrapper>
          </FormStyle.default.FieldsWrapper>
          {enableCustomPolicy && (
            <div className="submitPolicyButtonContainer">
              <SubmitPolicyButton
                customProps={customProps}
                i18n={i18n}
                checkFields={checkFields}
                values={values}
                validateForm={validateForm}
                submitAction={submitAction}
              />
            </div>
          )}
        </div>
      </div>
      <div className="section-wrapper">
        {enableCustomPolicy ? (
          <SubHeader className="customPolicy" variant="h2" text={i18n.t(CUSTOM_POLICIES)} />
        ) : (
          <SubHeader className="titlePolicy selectpolicy" variant="h2" text={i18n.t(SELECT_POLICIES)} />
        )}
        <div className="selectedCheckbox">
          {enableCustomPolicy ? (
            <div className="customPolicy">
              <div className="customPolicyTemplate">
                <div>
                  The custom policy document should be in Docx format and should follow the Custom Policy Document Template.
                </div>
                <div className="customPolicyTemplateButton">
                  <a
                    className="customPolicyTemplateLink"
                    href={CUSTOM_POLICY_TEMPLATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button label={i18n.t('common.customPolicyTemplate')} />
                  </a>
                </div>
              </div>
              <div className="uploadButtonContainer">
                <Button
                  className="buttonBulkUploadDoc"
                  type={'submit'}
                  label={i18n.t('dailyDocuments.uploadNewDoc')}
                  onClick={() => {
                    setbackgroundModal(false);
                    setwithCloseIcon(true);
                    setModalObject({
                      header: '',
                      content: () => {
                        return (
                          <UploadCustomPolicy
                            currentItem={currentItem}
                            closeModal={closeModal}
                            saveDocument={saveCustomPolicy}
                            uploadApi={UPLOAD_CUSTOM_POLICY}
                            callback={callback}
                            parent={CUSTOM_POLICY_PARENT}
                            languages={languages}
                          />
                        );
                      },
                    });
                    clickedModal();
                  }}
                />
              </div>

              <div className="customPolicyTableContainer">
                {isSpireon && (
                  <div className="driverstatusTitle">
                    <span className="spireonOldOnly">Custom Polices</span>
                  </div>
                )}
                <PolicyList
                  customPolicy={customPolicy}
                  editCustomPolicy={editCustomPolicy}
                  handleDeleteCustomPolicyAlert={handleDeleteCustomPolicyAlert}
                  callback={callback}
                  setwithCloseIcon={setwithCloseIcon}
                  setbackgroundModal={setbackgroundModal}
                  currentItem={currentItem}
                  setModalObject={setModalObject}
                  clickedModal={clickedModal}
                  closeModal={closeModal}
                  uploadApi={UPLOAD_CUSTOM_POLICY}
                  languages={languages}
                  downloadCustomPolicy={downloadCustomPolicy}
                ></PolicyList>
              </div>
            </div>
          ) : (
            customProps?.policies?.policies?.map((item, index) => (
              <Policy
                item={item}
                i18n={i18n}
                values={values}
                key={index}
                errFields={errFields}
                handleEdit={handleEdit}
                downloadDocument={customProps.downloadDocument}
                disabled={viewOnly}
              />
            ))
          )}
        </div>
      </div>
      {!viewOnly && !enableCustomPolicy && (
        <SubmitPolicyButton
          customProps={customProps}
          i18n={i18n}
          checkFields={checkFields}
          values={values}
          validateForm={validateForm}
          submitAction={submitAction}
        />
      )}
      {enableCustomPolicy && (
        <Modal.default
          open={open}
          setOpen={setOpen}
          data={
            ModalObject || {
              header: '',
              content: () => {},
            }
          }
          onCloseModal={closeModal}
          background="black"
          withCloseIcon={withCloseIcon}
          backgroundModal={backgroundModal}
          deletePopup
        />
      )}
    </>
  );
};

export default RenderComponent;

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  customProps: PropTypes.object,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
};
